import { useEffect, useState } from "react";
import { api, formatApiError, uploadFile, mediaSrc } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Camera, MapPin, Truck } from "lucide-react";

const CUSTOM = "__custom__";
const NONE = "__none__";

// Map a shop document (from API or context) into a form-shaped state object.
// Centralised so create / edit / refetch all hydrate identically and we never
// "lose" a saved field on remount.
const shopToForm = (s) => ({
  name: s?.name || "",
  description: s?.description || "",
  city: s?.city || "Abidjan",
  market: s?.market || "",
  location_detail: s?.location_detail || "",
  whatsapp: s?.whatsapp || "+225 ",
  phone: s?.phone || "+225 ",
  profile_image: s?.profile_image || null,
  cover_image: s?.cover_image || null,
  delivery_mode: s?.delivery_mode || "",
  delivery_zones: s?.delivery_zones || "",
  delivery_fee: s?.delivery_fee ?? "",
  free_delivery_from: s?.free_delivery_from ?? "",
  delivery_eta: s?.delivery_eta || "",
  pickup_available: !!s?.pickup_available,
  delivery_notes: s?.delivery_notes || "",
});

export default function CreateShop() {
  const { shop, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(() => shopToForm(null));
  const [cities, setCities] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [customCity, setCustomCity] = useState(false);
  const [customMarket, setCustomMarket] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/meta/cities").then(r => setCities(r.data || []));
  }, []);

  // Reload markets whenever the selected city changes (Phase 2: city-dependent markets)
  useEffect(() => {
    const params = form.city ? { city: form.city } : {};
    api.get("/meta/markets", { params }).then(r => setMarkets(r.data || [])).catch(() => setMarkets([]));
  }, [form.city]);

  // Hydrate from auth context (fast path)…
  useEffect(() => { if (shop) setForm(shopToForm(shop)); }, [shop]);

  // …and re-fetch fresh shop data on mount so we never display stale values
  // (covers cases where context wasn't refreshed after a previous save).
  useEffect(() => {
    let alive = true;
    api.get("/shops/mine").then(r => { if (alive && r.data) setForm(shopToForm(r.data)); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Detect custom city/market (value not in predefined list)
  useEffect(() => {
    if (cities.length && form.city && !cities.includes(form.city)) setCustomCity(true);
  }, [cities, form.city]);
  useEffect(() => {
    if (markets.length && form.market && !markets.includes(form.market)) setCustomMarket(true);
  }, [markets, form.market]);

  const uploadField = async (field, file) => {
    if (!file) return;
    try {
      const kind = field === "cover_image" ? "shop_cover" : "shop_profile";
      const url = await uploadFile(file, kind);
      setForm(f => ({ ...f, [field]: url }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'envoi de l'image");
    }
  };

  // Validate Côte d'Ivoire phone format. Returns null if valid, FR error if invalid.
  const validateCIPhone = (raw, label) => {
    const s = (raw || "").trim();
    if (!s) return null; // empty handled by `required` attribute on whatsapp
    if (!/^[\d+\s\-().]+$/.test(s)) return `Le ${label} contient des caractères invalides. Exemple : +225 07 00 00 00 00`;
    let digits = s.replace(/\D/g, "");
    if (digits.startsWith("225") && digits.length > 10) digits = digits.slice(3);
    if (digits.length !== 10) return `Le ${label} doit contenir 10 chiffres après l'indicatif. Exemple : +225 07 00 00 00 00`;
    if (!digits.startsWith("0")) return `Le ${label} doit commencer par 0 (ex : 07 00 00 00 00)`;
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    // Client-side phone validation BEFORE network request
    const waErr = validateCIPhone(form.whatsapp, "numéro WhatsApp");
    if (waErr) { toast.error(waErr); return; }
    // Phone is optional — only validate if non-empty (and not just the prefix)
    const phoneTrim = (form.phone || "").trim();
    const phoneIsJustPrefix = !phoneTrim || phoneTrim === "+225" || phoneTrim === "+225 ";
    if (!phoneIsJustPrefix) {
      const phErr = validateCIPhone(form.phone, "numéro de téléphone");
      if (phErr) { toast.error(phErr); return; }
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        phone: phoneIsJustPrefix ? null : form.phone,
        delivery_mode: form.delivery_mode || null,
        delivery_zones: form.delivery_zones.trim() || null,
        delivery_fee: form.delivery_fee === "" ? null : parseInt(form.delivery_fee) || 0,
        free_delivery_from: form.free_delivery_from === "" ? null : parseInt(form.free_delivery_from) || 0,
        delivery_eta: form.delivery_eta || null,
        pickup_available: !!form.pickup_available,
        delivery_notes: form.delivery_notes.trim() || null,
      };
      if (shop) {
        await api.patch(`/shops/${shop.id}`, payload);
        toast.success("Boutique mise à jour");
      } else {
        await api.post("/shops", payload);
        toast.success("Boutique créée !");
      }
      await refresh();
      nav("/tableau-bord");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-6 py-6">
      <h1 className="font-[Manrope] font-extrabold text-2xl">{shop ? "Ma boutique" : "Ouvrir ma boutique"}</h1>
      <p className="text-sm text-[#73706B] mt-1">Créez votre vitrine sur Forum CI pour toucher des milliers de clients.</p>

      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="shop-form">
        <div className="relative h-40 rounded-2xl overflow-hidden border border-[#E8E6E1] bg-[#F3F1EC]">
          {form.cover_image ? <img src={mediaSrc(form.cover_image)} alt="" className="w-full h-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-[#73706B] text-xs">Image de couverture</div>}
          <label className="absolute bottom-2 right-2 h-9 px-3 rounded-lg bg-black/70 text-white text-xs font-semibold flex items-center gap-1 cursor-pointer">
            <Camera className="h-4 w-4" /> Couverture
            <input type="file" accept="image/*" hidden onChange={(e) => uploadField("cover_image", e.target.files?.[0])} data-testid="upload-cover" />
          </label>
        </div>

        <div className="flex items-end gap-3 -mt-10 ml-3">
          <div className="relative w-20 h-20 rounded-2xl border-4 border-white bg-[#F3F1EC] overflow-hidden shadow">
            {form.profile_image ? <img src={mediaSrc(form.profile_image)} alt="" className="w-full h-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-xs text-[#73706B]">Logo</div>}
            <label className="absolute inset-0 cursor-pointer flex items-end justify-end p-1">
              <span className="h-6 w-6 rounded-full bg-[#E65100] text-white flex items-center justify-center"><Camera className="h-3 w-3" /></span>
              <input type="file" accept="image/*" hidden onChange={(e) => uploadField("profile_image", e.target.files?.[0])} data-testid="upload-profile" />
            </label>
          </div>
        </div>

        <div>
          <Label>Nom de la boutique *</Label>
          <Input data-testid="shop-name" required className="h-12 rounded-xl mt-1" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Chez Aya" />
          {shop && <div className="text-[11px] text-[#73706B] mt-1">Le nom est libre pendant 48h après la création, puis modifiable une fois tous les 14 jours.</div>}
        </div>
        <div>
          <Label>Description</Label>
          <Input data-testid="shop-description" className="h-12 rounded-xl mt-1" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Parlez de vos produits et de votre savoir-faire" />
        </div>

        {/* City — predefined + custom (Phase 1: always shows saved value, even before cities load) */}
        <div>
          <Label>Ville *</Label>
          {!customCity ? (
            <Select
              value={form.city || ""}
              onValueChange={(v) => { if (v === CUSTOM) { setCustomCity(true); setForm(f => ({ ...f, city: "" })); } else setForm(f => ({ ...f, city: v, market: "" })); }}
            >
              <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="shop-city"><SelectValue placeholder="Sélectionner une ville" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {/* Ensure saved city is always present even before cities[] loads */}
                {form.city && !cities.includes(form.city) && (
                  <SelectItem key={`saved-${form.city}`} value={form.city}>{form.city}</SelectItem>
                )}
                {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                <SelectItem value={CUSTOM}>✏️ Autre ville (saisir manuellement)</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex gap-2 mt-1">
              <Input data-testid="shop-city-custom" required className="h-12 rounded-xl flex-1" value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Saisissez votre ville" />
              <Button type="button" variant="outline" className="h-12 rounded-xl" onClick={() => setCustomCity(false)} data-testid="city-back-to-list">Liste</Button>
            </div>
          )}
        </div>

        {/* Market — depends on selected city */}
        <div>
          <Label>Marché</Label>
          {!customMarket ? (
            <Select
              value={form.market || NONE}
              onValueChange={(v) => {
                if (v === CUSTOM) { setCustomMarket(true); setForm(f => ({ ...f, market: "" })); }
                else if (v === NONE) setForm(f => ({ ...f, market: "" }));
                else setForm(f => ({ ...f, market: v }));
              }}
            >
              <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="shop-market"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Aucun</SelectItem>
                {/* Ensure saved market is always present even if not in current city's market list */}
                {form.market && !markets.includes(form.market) && (
                  <SelectItem key={`saved-${form.market}`} value={form.market}>{form.market}</SelectItem>
                )}
                {markets.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                <SelectItem value={CUSTOM}>✏️ Autre marché (saisir manuellement)</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex gap-2 mt-1">
              <Input data-testid="shop-market-custom" className="h-12 rounded-xl flex-1" value={form.market} onChange={(e) => setForm(f => ({ ...f, market: e.target.value }))} placeholder="Saisissez votre marché" />
              <Button type="button" variant="outline" className="h-12 rounded-xl" onClick={() => setCustomMarket(false)} data-testid="market-back-to-list">Liste</Button>
            </div>
          )}
          {form.city && markets.length === 0 && !customMarket && (
            <div className="text-[11px] text-[#73706B] mt-1">Aucun marché référencé pour {form.city}. Utilisez « Autre marché » pour saisir manuellement.</div>
          )}
        </div>

        {/* Location detail */}
        <div>
          <Label className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#E65100]" />Point de repère / Détail d'emplacement</Label>
          <Input
            data-testid="shop-location-detail"
            className="h-12 rounded-xl mt-1"
            value={form.location_detail}
            onChange={(e) => setForm(f => ({ ...f, location_detail: e.target.value }))}
            placeholder="Ex: À côté de la pharmacie, 2e étage boutique 14…"
          />
          <div className="text-[11px] text-[#73706B] mt-1">
            Ajoutez un point de repère pour aider les clients à vous retrouver plus facilement.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>WhatsApp *</Label>
            <Input data-testid="shop-whatsapp" required inputMode="tel" className="h-12 rounded-xl mt-1" value={form.whatsapp} onChange={(e) => setForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="+225 07 00 00 00 00" />
          </div>
          <div>
            <Label>Téléphone</Label>
            <Input data-testid="shop-phone" inputMode="tel" className="h-12 rounded-xl mt-1" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+225 07 00 00 00 00" />
          </div>
        </div>

        {/* Delivery section */}
        <div className="rounded-2xl border border-[#E8E6E1] bg-[#FFF8F3] dark:bg-[#2A1F17] p-4 space-y-3">
          <div className="flex items-center gap-2 font-[Manrope] font-bold text-[#1A1918]">
            <Truck className="h-4 w-4 text-[#E65100]" />
            Informations de livraison
          </div>
          <div className="text-[11px] text-[#73706B] -mt-1">Aidez vos clients à commander en toute confiance. Tous les champs sont optionnels.</div>

          <div>
            <Label>Mode de livraison</Label>
            <Select value={form.delivery_mode || "__none__"} onValueChange={(v) => setForm(f => ({ ...f, delivery_mode: v === "__none__" ? "" : v }))}>
              <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="shop-delivery-mode"><SelectValue placeholder="Non renseigné" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Non renseigné</SelectItem>
                <SelectItem value="livraison">Livraison uniquement</SelectItem>
                <SelectItem value="retrait">Retrait uniquement</SelectItem>
                <SelectItem value="livraison_retrait">Livraison + Retrait</SelectItem>
                <SelectItem value="aucune">Aucune livraison</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Zones desservies</Label>
            <Input data-testid="shop-delivery-zones" className="h-12 rounded-xl mt-1" value={form.delivery_zones} onChange={(e) => setForm(f => ({ ...f, delivery_zones: e.target.value }))} placeholder="Ex: Cocody, Marcory, Yopougon" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tarif de livraison (FCFA)</Label>
              <Input data-testid="shop-delivery-fee" inputMode="numeric" className="h-12 rounded-xl mt-1" value={form.delivery_fee} onChange={(e) => setForm(f => ({ ...f, delivery_fee: e.target.value }))} placeholder="Ex: 1500" />
            </div>
            <div>
              <Label>Gratuite dès (FCFA)</Label>
              <Input data-testid="shop-free-delivery-from" inputMode="numeric" className="h-12 rounded-xl mt-1" value={form.free_delivery_from} onChange={(e) => setForm(f => ({ ...f, free_delivery_from: e.target.value }))} placeholder="Ex: 25000" />
            </div>
          </div>

          <div>
            <Label>Délai estimé</Label>
            <Select value={form.delivery_eta || "__none__"} onValueChange={(v) => setForm(f => ({ ...f, delivery_eta: v === "__none__" ? "" : v }))}>
              <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="shop-delivery-eta"><SelectValue placeholder="Non renseigné" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Non renseigné</SelectItem>
                <SelectItem value="Moins de 2h">Moins de 2h</SelectItem>
                <SelectItem value="24h">Sous 24h</SelectItem>
                <SelectItem value="48h">Sous 48h</SelectItem>
                <SelectItem value="3-5j">3 à 5 jours</SelectItem>
                <SelectItem value="1 semaine +">1 semaine et +</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between py-1">
            <Label htmlFor="pickup-toggle" className="text-sm font-semibold">Retrait sur place disponible</Label>
            <Switch id="pickup-toggle" data-testid="shop-pickup-available" checked={!!form.pickup_available} onCheckedChange={(v) => setForm(f => ({ ...f, pickup_available: v }))} />
          </div>

          <div>
            <Label>Détails supplémentaires</Label>
            <Textarea data-testid="shop-delivery-notes" rows={3} className="rounded-xl mt-1" value={form.delivery_notes} onChange={(e) => setForm(f => ({ ...f, delivery_notes: e.target.value }))} placeholder="Ex: Livraison uniquement en semaine de 9h à 17h. Paiement à la livraison accepté." />
          </div>
        </div>

        <Button type="submit" disabled={loading} data-testid="shop-submit" className="w-full h-12 rounded-xl bg-[#E65100] hover:bg-[#C44100] font-bold">
          {loading ? "Enregistrement…" : shop ? "Enregistrer" : "Créer la boutique"}
        </Button>
      </form>
    </div>
  );
}
